import React, { useEffect, useState } from "react";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { Modal, Field } from "./Fields";
import { api, fmtUSD, fmtNum } from "../lib/api";
import { Link } from "react-router-dom";
import { Plus, Users, Pencil, Trash2, FileText, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const blank = { name: "", company: "", email: "", phone: "", address: "", notes: "", default_split_pct: 50 };

export default function Partners() {
  const [partners, setPartners] = useState([]);
  const [summaries, setSummaries] = useState({});
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get("/partners");
    setPartners(data);
    // Fetch summaries in parallel for outstanding balances
    const sums = await Promise.all(data.map((p) => api.get(`/partners/${p.id}/summary`).then((r) => [p.id, r.data]).catch(() => [p.id, null])));
    const map = {};
    for (const [id, s] of sums) if (s) map[id] = s;
    setSummaries(map);
  };

  useEffect(() => { load(); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, default_split_pct: parseFloat(form.default_split_pct) || 0 };
    try {
      if (editing) {
        await api.put(`/partners/${editing.id}`, payload);
        toast.success("Partner updated");
      } else {
        await api.post("/partners", payload);
        toast.success("Partner added");
      }
      setOpen(false);
      setEditing(null);
      setForm(blank);
      load();
    } catch {
      toast.error("Could not save partner");
    }
  };

  const onEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name, company: p.company || "", email: p.email || "", phone: p.phone || "",
      address: p.address || "", notes: p.notes || "", default_split_pct: p.default_split_pct ?? 50,
    });
    setOpen(true);
  };

  const onDelete = async (id) => {
    if (!window.confirm("Delete this partner? Their fields will be detached but expenses stay in your ledger.")) return;
    await api.delete(`/partners/${id}`);
    toast.success("Partner deleted");
    load();
  };

  return (
    <div>
      <TopBar
        title="Partners"
        subtitle="Anyone you cost-share a field with. Add them here, then attach to a Field with a split %."
        actions={
          <div className="flex items-center gap-2 mt-6">
            <Link
              to="/partner-invoices"
              data-testid="partner-invoices-link"
              className="border border-[#1F1E1B] px-4 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors flex items-center gap-2"
            >
              <FileText className="w-4 h-4" /> Invoices
            </Link>
            <button
              data-testid="add-partner-btn"
              onClick={() => { setEditing(null); setForm(blank); setOpen(true); }}
              className="bg-[#BC4727] text-white px-5 py-2 font-bold uppercase tracking-wider text-sm hover:bg-[#9D391E] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Partner
            </button>
          </div>
        }
      />

      {partners.length === 0 ? (
        <EmptyState icon={Users} title="No partners yet" hint="Add Uncle (or anyone you share costs with) — then attach to any field with a split %." testid="empty-partners" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {partners.map((p) => {
            const s = summaries[p.id];
            const outstanding = s?.totals?.outstanding || 0;
            const fieldCount = s?.totals?.field_count || 0;
            return (
              <div key={p.id} className="bg-white border border-[#1F1E1B] p-5 card-hover" data-testid={`partner-card-${p.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <h3 className="font-display text-xl font-bold">{p.name}</h3>
                    {p.company && <div className="text-xs text-[#66645E]">{p.company}</div>}
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-[0.18em] text-[#66645E]">Default Split</div>
                    <div className="font-display text-2xl font-black tabular">{fmtNum(p.default_split_pct, 1)}%</div>
                  </div>
                </div>

                <div className="border-t border-[#E5E2D6] pt-3 space-y-1 text-sm">
                  {p.email && <div className="flex items-center gap-2"><Mail className="w-3 h-3 text-[#66645E]" /> {p.email}</div>}
                  {p.phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3 text-[#66645E]" /> {p.phone}</div>}
                  {p.address && <div className="text-[#66645E]">{p.address}</div>}
                </div>

                {s && (
                  <div className="grid grid-cols-3 gap-2 mt-3 pt-3 border-t border-[#E5E2D6] text-center">
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-[#66645E]">Fields</div>
                      <div className="font-bold tabular">{fieldCount}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-[#66645E]">Billed</div>
                      <div className="font-bold tabular">{fmtUSD(s.totals.billed)}</div>
                    </div>
                    <div>
                      <div className="text-[9px] uppercase tracking-wider text-[#66645E]">Outstanding</div>
                      <div className={`font-bold tabular ${outstanding > 0.005 ? "text-[#BC4727]" : "text-[#1A4135]"}`}>{fmtUSD(outstanding)}</div>
                    </div>
                  </div>
                )}

                <div className="flex gap-2 mt-4 pt-3 border-t border-[#E5E2D6]">
                  <Link
                    to={`/partner-invoices?partner_id=${p.id}`}
                    data-testid={`bill-partner-${p.id}`}
                    className="flex-1 bg-[#1A4135] text-white py-1.5 text-xs font-bold uppercase tracking-wider hover:bg-[#133128] transition-colors flex items-center justify-center gap-1.5"
                  >
                    <FileText className="w-3 h-3" /> Generate Invoice
                  </Link>
                  <button data-testid={`edit-partner-${p.id}`} onClick={() => onEdit(p)} className="border border-[#1F1E1B] py-1.5 px-3 text-xs font-bold uppercase tracking-wider hover:bg-[#1F1E1B] hover:text-white transition-colors">
                    <Pencil className="w-3 h-3" />
                  </button>
                  <button data-testid={`delete-partner-${p.id}`} onClick={() => onDelete(p.id)} className="border border-[#BC4727] text-[#BC4727] py-1.5 px-3 text-xs font-bold uppercase tracking-wider hover:bg-[#BC4727] hover:text-white transition-colors">
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <Modal title={editing ? "Edit Partner" : "New Partner"} onClose={() => setOpen(false)}>
          <form onSubmit={onSubmit} className="space-y-4" data-testid="partner-form">
            <Field label="Name" required>
              <input data-testid="partner-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Uncle Bill" className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Company (optional)">
                <input data-testid="partner-company-input" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Default Split % (their share)">
                <input data-testid="partner-split-input" type="number" step="0.5" min="0" max="100" value={form.default_split_pct} onChange={(e) => setForm({ ...form, default_split_pct: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm tabular" />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Email">
                <input data-testid="partner-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
              <Field label="Phone">
                <input data-testid="partner-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
              </Field>
            </div>
            <Field label="Address">
              <textarea data-testid="partner-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <Field label="Notes">
              <textarea data-testid="partner-notes-input" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} className="w-full bg-white border border-[#1F1E1B] px-3 py-2 text-sm" />
            </Field>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setOpen(false)} data-testid="partner-cancel-btn" className="border border-[#1F1E1B] px-5 py-2 text-sm font-bold uppercase tracking-wider">Cancel</button>
              <button type="submit" data-testid="partner-submit-btn" className="bg-[#1A4135] text-white px-5 py-2 text-sm font-bold uppercase tracking-wider hover:bg-[#133128]">Save Partner</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
